<template>
  <div class="bar-x-chart-viz" />
</template>

<script>
import * as d3 from 'd3';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

am4core.addLicense('CH245905005');
am4core.addLicense('TL245443410');
am4core.useTheme(am4themesAnimated);

export default {
  name: 'BarChartX',
  props: {
    country: {
      type: String,
      required: true,
    },
  },
  watch: {
    country(newVal) {
      this.filterChart(newVal);
    },
  },
  mounted() {
    // load data
    const dataSource = new am4core.DataSource();
    dataSource.url = `${process.env.BASE_URL}data/bar-x-chart-data.csv`;
    dataSource.parser = new am4core.CSVParser();
    dataSource.parser.options.emptyAs = '';
    dataSource.parser.options.useColumnNames = true;

    dataSource.load();

    dataSource.events.on('done', ({ data }) => {
      // sort type prop by alphabet
      const rawData = data.sort((a, b) => (a.country === b.country ? 0 : +(a.country > b.country) || -1));
      // set default "type"
      this.typeDefault = rawData[0].country;

      // prepare data
      this.sanitizedData = this.formulateData(rawData);

      // pass data to parent
      this.$emit('data-sanitized', rawData);
      // this.$emit('country', this.typeDefault);

      // create chart
      this.chart = this.createChart();

      // create axes
      this.categoryAxis = this.createAxisY();
      this.valueAxis = this.createAxisX();

      // create series
      this.series = this.createSeries();

      // set chart default data
      this.setChartData(this.typeDefault);

      this.filterChart(this.typeDefault);
    });
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    formulateData(rawData) {
      // convert percentage to Number
      const data = rawData.map(({ percentage, ...props }) => {
        return { ...props, percentage: Number(percentage) };
      });

      // group by type
      const dataByType = d3.group(data, (d) => d.country);

      // convert Map to Array
      const dataByTypeArr = Array.from(dataByType, ([key, value]) => ({ key, value }));

      // sort the data
      // dataByTypeArr.forEach((el) => {
      //   // sort by percentage
      //   el.value.sort((a, b) => d3.descending(a.percentage, b.percentage));
      // });

      const sorter = [
        'Makes payments easier',
        'Instant payments',
        'Reward points/discounts',
        "I don't have to carry cash",
        'Investments and trading',
        'Buy now, pay later options',
        'Integrated insurance services',
        'Point-of-service lending',
      ];

      dataByTypeArr.forEach((el) => {
        el.value.sort((a, b) => sorter.indexOf(a.reasons) - sorter.indexOf(b.reasons));
      });

      // convert back to Map
      const sanitizedData = new Map(dataByTypeArr.map((item) => [item.key, item.value]));

      return sanitizedData;
    },
    createChart() {
      // create XY chart
      const chart = am4core.create(this.$el, am4charts.XYChart);

      // set initial chart data
      chart.data = this.sanitizedData.get(this.type);

      // settings
      chart.padding(20, 10, 25, 0);
      chart.zoomOutButton.disabled = true;

      return chart;
    },
    createAxisY() {
      // y-axis - category axis
      const categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());
      // logic settings
      categoryAxis.dataFields.category = 'reasons';
      categoryAxis.renderer.inversed = true;
      // style settings
      categoryAxis.renderer.grid.template.disabled = true;
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.line.strokeOpacity = 0;
      // cell width
      categoryAxis.renderer.cellStartLocation = 0.15;
      categoryAxis.renderer.cellEndLocation = 0.85;
      // labels
      categoryAxis.renderer.labels.template.location = 0.45;
      categoryAxis.renderer.labels.template.fontSize = 16;
      categoryAxis.renderer.labels.template.fill = '#121212';
      categoryAxis.renderer.labels.template.align = 'left';

      if (this.$breakpoint.mobile) {
        categoryAxis.renderer.labels.template.dy = 7;
        categoryAxis.renderer.labels.template.fontSize = 12.5;
        categoryAxis.renderer.labels.template.wrap = true;
        categoryAxis.renderer.labels.template.maxWidth = 130;
      }

      return categoryAxis;
    },
    createAxisX() {
      // x-axis - value axis
      const valueAxis = this.chart.xAxes.push(new am4charts.ValueAxis());
      valueAxis.min = -1;
      valueAxis.max = 105;
      valueAxis.strictMinMax = true;

      if (this.$breakpoint.mobile) {
        valueAxis.max = 100;
      }

      valueAxis.renderer.grid.template.strokeWidth = 0;
      valueAxis.renderer.labels.template.disabled = true;
      valueAxis.renderer.line.strokeOpacity = 0;
      valueAxis.renderer.baseGrid.disabled = true;

      return valueAxis;
    },
    createSeries() {
      const series = this.chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueX = 'percentage';
      series.dataFields.categoryY = 'reasons';
      series.columns.template.strokeWidth = 0;
      series.zIndex = 2;

      series.columns.template.adapter.add('fill', function (fill, target) {
        const colorMap = {
          'Makes payments easier': '#005EB8',
          'Instant payments': '#E25A5F',
          'Reward points/discounts': '#FDC4D0',
          "I don't have to carry cash": '#A295E2',
          'Investments and trading': '#F6C76A',
          'Buy now, pay later options': '#F09252',
          'Integrated insurance services': '#95D5A3',
          'Point-of-service lending': '#8C7D6E',
        };

        const getColor = colorMap[target.dataItem.dataContext.reasons];
        return getColor;
      });

      series.columns.template.column.cornerRadiusTopLeft = 4;
      series.columns.template.column.cornerRadiusTopRight = 4;
      series.columns.template.column.cornerRadiusBottomLeft = 4;
      series.columns.template.column.cornerRadiusBottomRight = 4;

      // create value label bullets
      const valueLabel = series.bullets.push(new am4charts.LabelBullet());
      valueLabel.label.text = '{percentage}%';
      valueLabel.label.truncate = false;
      valueLabel.label.hideOversized = false;
      valueLabel.label.horizontalCenter = 'left';
      valueLabel.label.dx = 10;
      valueLabel.label.fontSize = 14;
      valueLabel.label.fill = '#373839';

      series.columns.template.tooltipHTML = `<p class="text-legend font-weight-medium" style="margin-bottom: 5px">{categoryY}</p><p class="text-small mb-0">{valueX}% from ${this.country}</p>`;

      series.tooltip.background.cornerRadius = 4;
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = '#FFFFFF';
      series.tooltip.label.fill = '#373839';
      series.tooltip.background.pointerLength = 0;
      series.tooltip.label.maxWidth = 205;
      series.tooltip.label.wrap = true;
      series.tooltip.background.fillOpacity = 1;
      series.tooltip.background.cornerRadius = 10;

      const hoverState = series.columns.template.states.create('hover');
      hoverState.properties.stroke = am4core.color('#201113');
      hoverState.properties.strokeWidth = 2;

      series.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;

      return series;
    },
    setChartData(country) {
      // get data set by type
      const selectedSet = this.sanitizedData.get(country);
      const selectedData = selectedSet.map(({ category, percentage }) => {
        return { category, percentage };
      });

      // calculate data count difference
      const newDataCount = selectedData.length - this.series.data.length;

      // set category data
      this.categoryAxis.data = selectedSet.map(({ reasons }) => {
        return { reasons };
      });

      // set series data
      if (this.series.data.length <= 0) {
        // set inital data
        this.series.data = selectedData;
      } else {
        // check if add or remove data
        if (newDataCount < 0) {
          this.series.removeData(Math.abs(newDataCount));
        } else {
          // create new empty data by count
          const newEmptyData = [...new Array(newDataCount)].map(() => {
            return { category: '', percentage: 0 };
          });

          this.series.addData(newEmptyData);
        }

        // update data values
        this.series.data.forEach((d, i) => {
          const { reasons, percentage } = selectedSet.find((newData, newIdx) => i === newIdx);
          d.reasons = reasons;
          d.percentage = percentage;
        });

        this.series.columns.template.tooltipHTML = `<p class="text-legend font-weight-medium" style="margin-bottom: 5px">{categoryY}</p><p class="text-small mb-0">{valueX}% from ${country}</p>`;
      }
    },
    filterChart(country) {
      this.setChartData(country);
      this.series.invalidateRawData();
    },
  },
};
</script>

<style lang="scss" scoped>
.bar-x-chart-viz {
  width: 100%;
  height: 480px;
}
</style>
